import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './dashboard.module.scss';

/**
 * The tab strip across the top of the super-admin dashboard.
 *
 * Six labels — one of them "Subscription / Asset Class Distribution" — do not
 * fit, and the previous strip simply set `overflow-x` and let a native
 * scrollbar sit under them. The last tab was sliced mid-word with nothing
 * indicating there was more, so people did not know the sixth tab existed.
 *
 * Arrows appear only when the strip actually overflows, and the active tab is
 * scrolled into view on mount so a deep link never lands on a hidden tab.
 */

const Chevron = ({direction}) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

export const TabRail = ({tabs, activeTab, onChange}) => {
  const scrollerRef = useRef(null);
  const [overflow, setOverflow] = useState({left: false, right: false});

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setOverflow({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  // A tab restored from the URL can sit off-screen; bring it into view.
  // Centred rather than 'nearest', which parked it flush against the edge where
  // the scroll arrow then covered half the label.
  useEffect(() => {
    const el = scrollerRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({block: 'nearest', inline: 'center'});
  }, [activeTab]);

  const nudge = amount => {
    scrollerRef.current?.scrollBy({left: amount, behavior: 'smooth'});
  };

  return (
    <div className={styles.tabRail}>
      {overflow.left && (
        <button
          type="button"
          className={styles.railArrow}
          aria-label="Scroll tabs left"
          onClick={() => nudge(-220)}>
          <Chevron direction="left" />
        </button>
      )}

      <div className={styles.tabScroller} ref={scrollerRef} role="tablist">
        {tabs.map(tab => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            data-active={activeTab === tab.value}
            aria-selected={activeTab === tab.value}
            className={`${styles.tab} ${
              activeTab === tab.value ? styles.tabActive : ''
            }`}
            onClick={() => onChange(tab.value)}>
            {tab.label}
          </button>
        ))}
      </div>

      {overflow.right && (
        <button
          type="button"
          className={styles.railArrow}
          aria-label="Scroll tabs right"
          onClick={() => nudge(220)}>
          <Chevron direction="right" />
        </button>
      )}
    </div>
  );
};

export default TabRail;
